import { useEffect, useState, useCallback, useRef } from "react";
import { Autocomplete, Button, Input, MultiSelect } from "src/components";
import crossIcon from "../../assets/cross.svg";
import { useToast } from "src/hooks/useToast";
import { useEventStatus } from "src/hooks/useEventStatus";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import {
  getRejectionCategory,
  getRejectPreflight,
  submitRejectInvoice,
  submitRejectionReEditAction,
  getRejectStockistInvoices,
  getRejectPrefillData,
  getRejectProductPrice,
  getInvoicePriceList,
  getSimilarCompanyProducts,
  postInvoiceRemark,
  getPoRefNoListing,
} from "src/services/invoice-api";
import { queryDivisionDetails } from "src/services/division-api";

// ─── Local Types ─────────────────────────────────────────────────────────────

interface InputFieldDefinition {
  key: string;
  type: "text" | "date" | "selection" | "multiselect";
  label: string;
  prefill_source?: "textract_invoice_number" | "textract_dl" | "textract_gst" | "master_dl" | "master_gst";
  selection_source?: "stockist_invoices" | "company_products" | "divisions";
}

interface RejectionCategory {
  rejection_inv_category_id: number;
  rejection_code: string;
  rejection_header: string;
  rejection_remark: string;
  mandatory_input_fields: InputFieldDefinition[];
  corrective_hint: string;
  is_escalation: boolean;
  fuid_status_id: number;
  is_review_required: boolean;
}

interface PrefillData {
  invoice_number: string | null;
  retailer_dl_number: string | null;
  retailer_gst: string | null;
  counter_dl_number: string | null;
  counter_gst_number: string | null;
}

interface PriceComparison {
  inv_mrp: number | null;
  ptr_billed: number | null;
  ptr_as_per_company: number | null;
  gst: number | null;
}

// Fallback price row from the company master (/get-product-price), used when
// the product isn't on the invoice and reject-product-price returns HTTP 400.
interface ProductPriceOption {
  company_productprice_id?: number | string;
  mrp: number | null;
  ptr: number | null;
  gst: number | null;
}

interface PreflightResponse {
  rejection_code: string;
  rejection_header: string;
  rejection_remark: string;
  sub_remarks: string;
  corrective_hint: string;
  is_escalation: boolean;
  mandatory_input_fields: InputFieldDefinition[];
  price_comparison?: PriceComparison;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getPrefillValue = (source: string | undefined, prefillData: PrefillData | null): string => {
  if (!prefillData || !source) return "";
  switch (source) {
    case "textract_invoice_number": return prefillData.invoice_number ?? "";
    case "textract_dl":             return prefillData.retailer_dl_number ?? "";
    case "textract_gst":            return prefillData.retailer_gst ?? "";
    case "master_dl":               return prefillData.counter_dl_number ?? "";
    case "master_gst":              return prefillData.counter_gst_number ?? "";
    default:                        return "";
  }
};

// Reference label shown beside textract_dl / textract_gst fields
const getReferenceLabel = (
  source: string | undefined,
  prefillData: PrefillData | null,
): string | null => {
  if (!prefillData || !source) return null;
  if (source === "textract_dl" && prefillData.counter_dl_number)
    return `Counter DL: ${prefillData.counter_dl_number}`;
  if (source === "textract_gst" && prefillData.counter_gst_number)
    return `Counter GST: ${prefillData.counter_gst_number}`;
  return null;
};

const PRC_CODES = ["PRC-001", "PRC-002"];

// Order-number fields (e.g. ORD-003) carry no prefill/selection source, so match
// on key/label like the invoice-number field does.
const isOrderNumberField = (f: InputFieldDefinition) =>
  f.type !== "date" &&
  (/order.?(number|no|code)/i.test(f.key || "") || /order.?(number|no|code)/i.test(f.label || ""));

// Company-name fields (e.g. ORD-004) — auto-picked from the FUID's company.
const isCompanyNameField = (f: InputFieldDefinition) =>
  f.type === "text" && (/company.?name/i.test(f.key || "") || /company.?name/i.test(f.label || ""));

// Customer PO fields (e.g. PO-001) — sourced from the reference order's
// poid_ref_number / poid_date. Checked before the invoice-number/date matchers,
// which would otherwise claim them (any date field reads as an invoice date).
const PO_NUMBER_RE = /customer.?po|po.?ref|po.?(number|no)\b/i;
const isPoNumberField = (f: InputFieldDefinition) =>
  f.type !== "date" &&
  ((f.selection_source as string) === "order_po_numbers" || PO_NUMBER_RE.test(f.label || "") || PO_NUMBER_RE.test(f.key || ""));
const isPoDateField = (f: InputFieldDefinition) =>
  f.type === "date" && (/po.?date/i.test(f.label || "") || /po.?date/i.test(f.key || ""));

// Some categories are configured with two fields sharing one key (PO-001's
// Customer PO Number and PO Date) — they'd share one value in mandatoryInputs.
// Give each repeat a unique key so every field holds its own value.
const dedupeFieldKeys = (cat: RejectionCategory): RejectionCategory => {
  const seen = new Set<string>();
  const fields = (cat.mandatory_input_fields ?? []).map((f, idx) => {
    if (!seen.has(f.key)) {
      seen.add(f.key);
      return f;
    }
    let key = isPoDateField(f) ? "po_date" : `${f.key}_${idx}`;
    if (seen.has(key)) key = `${key}_${idx}`;
    seen.add(key);
    return { ...f, key };
  });
  return { ...cat, mandatory_input_fields: fields };
};

// Map a /get-product-price master row into the fallback option shape.
const mapPriceOption = (item: any): ProductPriceOption => ({
  company_productprice_id: item?.company_productprice_id,
  mrp: item?.mrp != null && item?.mrp !== "" ? Number(item.mrp) : null,
  ptr: item?.ptr != null && item?.ptr !== "" ? Number(item.ptr) : null,
  gst:
    item?.gst_pc != null && item?.gst_pc !== ""
      ? Number(String(item.gst_pc).replace("%", "").trim())
      : null,
});

// Project a fallback master price row into the price-comparison shape the UI
// renders. There's no billed PTR (the product isn't on the invoice), so the
// master PTR populates the "PTR (Company)" column.
const priceOptionToComparison = (opt: ProductPriceOption): PriceComparison => ({
  inv_mrp: opt.mrp,
  ptr_billed: null,
  ptr_as_per_company: opt.ptr,
  gst: opt.gst,
});

const formatDateForInput = (dateString: any): string => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

// ─── Component ───────────────────────────────────────────────────────────────

const InvoiceReject = ({
  onCloseModal,
  invoiceData,
  tab,
  mode = "reject",
  onSuccess,
}: {
  onCloseModal: () => void;
  invoiceData: any;
  tab?: string;
  // "rename" reuses this UI to edit an already-stamped rejection; submits to
  // the RE_EDIT_ACTION event (minimal write, no master/V1/Novu) instead of REJECT.
  mode?: "reject" | "rename";
  // Fired after a successful submission so callers can navigate / refetch.
  // Invoked before onCloseModal.
  onSuccess?: () => void;
}) => {
  const { InvoiceFilters, InvoiceApprovalFilters } = useSelector((state: RootState) => state.invoice);
  const { idTokenDetails } = useSelector((state: RootState) => state.auth);
  const company_code =
    invoiceData?.company_code ??
    (tab === "entry"
      ? InvoiceFilters?.company?.[0]?.code
      : InvoiceApprovalFilters?.company?.[0]?.code);

  const { toast } = useToast();
  const { withEventCheck } = useEventStatus();

  // ── Step state ─────────────────────────────────────────────────────────────
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // ── Step 1 state ───────────────────────────────────────────────────────────
  // const [allCategories, setAllCategories] = useState<RejectionCategory[]>([]);
  const [grouped, setGrouped] = useState<Record<string, RejectionCategory[]>>({});
  const [selectedCategory, setSelectedCategory] = useState<RejectionCategory | null>(null);

  // ── Step 2 state ───────────────────────────────────────────────────────────
  const [mandatoryInputs, setMandatoryInputs] = useState<Record<string, any>>({});
  // M10-15732: lets the product-code selection below jump focus straight to the next
  // mandatory field (e.g. MRP) instead of leaving the user to click into it manually.
  const mandatoryFieldInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [prefillData, setPrefillData] = useState<PrefillData | null>(null);
  const [stockistInvoices, setStockistInvoices] = useState<string[]>([]);
  const [divisions, setDivisions] = useState<any[]>([]);
  // PO records raised for this counter/location (po_ref_listing) — offered in the Customer PO dropdown.
  const [listedPoRefs, setListedPoRefs] = useState<{ po_number: string; po_date: string; order_code: string }[]>([]);
  const [productPrice, setProductPrice] = useState<PriceComparison | null>(null);
  // Fallback master price rows (from /get-product-price) shown when the product
  // isn't on the invoice. >1 row → reviewer picks which MRP/PTR applies.
  const [priceOptions, setPriceOptions] = useState<ProductPriceOption[]>([]);
  const [selectedPriceIdx, setSelectedPriceIdx] = useState(0);
  // True when the billed PTR couldn't be derived (no row on invoice, or the
  // endpoint returned 0/null) → the reviewer must type it. Kept as a flag, not
  // derived from the value, so the input doesn't disappear once they type.
  const [needPtrBilled, setNeedPtrBilled] = useState(false);
  const [isFetchingPrice, setIsFetchingPrice] = useState(false);
  const ptrBilledInputRef = useRef<HTMLInputElement>(null);

  // M10-15727 Once a product is picked and the PTR Billed field appears (couldn't be
  // auto-derived), move focus there so the reviewer can type it right away.
  useEffect(() => {
    if (needPtrBilled) {
      ptrBilledInputRef.current?.focus();
    }
  }, [needPtrBilled]);

  // ── Step 3 state ───────────────────────────────────────────────────────────
  const [preflight, setPreflight] = useState<PreflightResponse | null>(null);

  // ── Shared loading ─────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);

  const siaId: number = invoiceData?.stockist_invoice_attachment_id;

  // Reference orders mapped to this FUID (stockist_ordercode_mapping). One →
  // prefilled into the order-number field; more than one → offered as a dropdown.
  const fuidOrderCodes: string[] = Array.from(
    new Set<string>(
      (invoiceData?.orderCodes ?? [])
        .map((c: any) => String(c?.order_code ?? "").trim())
        .filter(Boolean),
    ),
  );

  // Customer PO number + date from those reference orders, deduped by PO number.
  // One → prefilled; more than one → dropdown, picking a PO also fills its date.
  const fuidPoRefs: { po_number: string; po_date: string; order_code: string }[] = [];
  for (const c of invoiceData?.orderCodes ?? []) {
    const po_number = String(c?.reference_order?.poid_ref_number ?? "").trim();
    if (!po_number || fuidPoRefs.some((p) => p.po_number === po_number)) continue;
    fuidPoRefs.push({
      po_number,
      po_date: formatDateForInput(c?.reference_order?.poid_date),
      order_code: c?.order_code ?? "",
    });
  }
  // Dropdown options: FUID-mapped POs first, then po_ref_listing results, deduped by PO number.
  const poOptions = [...fuidPoRefs];
  for (const p of listedPoRefs) {
    if (!poOptions.some((o) => o.po_number === p.po_number)) poOptions.push(p);
  }
  // A PO date field sharing its key with the PO number field (misconfigured
  // category) would overwrite the number — never write a date into that key.
  const poNumberFieldKeys = (selectedCategory?.mandatory_input_fields ?? []).filter(isPoNumberField).map((f) => f.key);
  const poDateFieldKeys = (selectedCategory?.mandatory_input_fields ?? [])
    .filter((f) => isPoDateField(f) && !poNumberFieldKeys.includes(f.key))
    .map((f) => f.key);

  // ── Fetch categories on mount ──────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await getRejectionCategory(company_code);
        // setAllCategories(res?.data?.data ?? []);
        const raw: Record<string, RejectionCategory[]> = res?.data?.grouped ?? {};
        setGrouped(
          Object.fromEntries(Object.entries(raw).map(([g, cats]) => [g, (cats ?? []).map(dedupeFieldKeys)])),
        );
      } catch {
        toast({ description: "Failed to load rejection categories", variant: "destructive" });
      }
    };
    load();
  }, []);

  // ── RENAME mode prefill: once categories are loaded, auto-select the
  // category matching the invoice's existing rejection_code and seed any
  // mandatory_inputs from rejection_data_email_desc so the reviewer is
  // editing the current rejection, not re-entering from scratch.
  // Runs at most once (via the ref) — gating on `selectedCategory` being falsy
  // instead would re-fire and silently restore the old category every time the
  // reviewer clears the field while editing.
  const hasSeededRenameRef = useRef(false);
  useEffect(() => {
    if (mode !== "rename") return;
    if (hasSeededRenameRef.current) return;
    const existingCode = invoiceData?.rejection_code;
    if (!existingCode) return;
    const all = Object.values(grouped).flat();
    const match = all.find((c) => c.rejection_code === existingCode);
    if (!match) return; // grouped hasn't loaded yet — retry on the next run
    hasSeededRenameRef.current = true;
    setSelectedCategory(match);
    const emailDesc = invoiceData?.rejection_data_email_desc || {};
    if (emailDesc && typeof emailDesc === "object") {
      const seeded: Record<string, any> = {};
      for (const field of match.mandatory_input_fields || []) {
        if (emailDesc[field.key] !== undefined && emailDesc[field.key] !== null) {
          seeded[field.key] = emailDesc[field.key];
        }
      }
      if (Object.keys(seeded).length > 0) {
        setMandatoryInputs((prev) => ({ ...seeded, ...prev }));
      }
    }
  }, [mode, grouped, invoiceData]);

  // ── Fetch prefill + stockist invoices when entering Step 2 ─────────────────
  useEffect(() => {
    if (step !== 2 || !selectedCategory) return;

    const hasMandatoryFields = selectedCategory.mandatory_input_fields?.length > 0;
    if (!hasMandatoryFields) return;

    // Synchronous auto-fetch from invoiceData — applies immediately on every Step 2 entry
    const selectedInvoiceNumber = invoiceData?.invoice?.invoice_number;
    const formattedInvoiceDate = formatDateForInput(invoiceData?.invoice?.new_invoice_date);
    const isInvoiceNumberField = (f: InputFieldDefinition) =>
      f.prefill_source === "textract_invoice_number" ||
      f.selection_source === "stockist_invoices" ||
      /invoice.?(number|no)/i.test(f.key || "") ||
      /invoice.?(number|no)/i.test(f.label || "");
    const isInvoiceDateField = (f: InputFieldDefinition) =>
      f.type === "date" || /invoice.?date/i.test(f.key || "") || /invoice.?date/i.test(f.label || "");
    const syncPrePopulated: Record<string, any> = {};
    for (const field of selectedCategory.mandatory_input_fields) {
      if (isPoNumberField(field) || isPoDateField(field)) {
        if (fuidPoRefs.length === 1 && (isPoNumberField(field) || poDateFieldKeys.includes(field.key))) {
          const val = isPoDateField(field) ? fuidPoRefs[0].po_date : fuidPoRefs[0].po_number;
          if (val) syncPrePopulated[field.key] = val;
        }
      } else if (isInvoiceNumberField(field) && selectedInvoiceNumber) {
        syncPrePopulated[field.key] = selectedInvoiceNumber;
      } else if (isInvoiceDateField(field) && formattedInvoiceDate) {
        syncPrePopulated[field.key] = formattedInvoiceDate;
      } else if (isOrderNumberField(field) && fuidOrderCodes.length === 1) {
        syncPrePopulated[field.key] = fuidOrderCodes[0];
      } else if (isCompanyNameField(field) && invoiceData?.company_name) {
        syncPrePopulated[field.key] = invoiceData.company_name;
      }
    }
    if (Object.keys(syncPrePopulated).length > 0) {
      setMandatoryInputs((prev) => ({ ...syncPrePopulated, ...prev }));
    }

    // Async prefill (textract / master DL & GST) — fills text fields that haven't been touched
    const loadPrefill = async () => {
      try {
        const res = await getRejectPrefillData({ stockist_invoice_attachment_id: siaId }, company_code);
        const data: PrefillData = res?.data?.data ?? {};
        setPrefillData(data);
        const prePopulated: Record<string, any> = {};
        for (const field of selectedCategory.mandatory_input_fields) {
          if (field.prefill_source && field.type === "text") {
            const val = getPrefillValue(field.prefill_source, data);
            if (val) prePopulated[field.key] = val;
            else if (field.prefill_source === "textract_invoice_number" && selectedInvoiceNumber) {
              prePopulated[field.key] = selectedInvoiceNumber;
            }
          }
          // Auto-map invoice number even when the field has no explicit prefill_source
          // (e.g. EIN-005, MST-007): use the created invoice number, else the
          // textract-extracted one from the prefill payload.
          if (isInvoiceNumberField(field) && !isPoNumberField(field) && !prePopulated[field.key]) {
            const inv = selectedInvoiceNumber || data?.invoice_number;
            if (inv) prePopulated[field.key] = inv;
          }
        }
        setMandatoryInputs((prev) => ({ ...prePopulated, ...prev }));
      } catch {
        /* non-blocking */
      }
    };

    // Stockist invoices for INV-001 dropdown
    const loadStockistInvoices = async () => {
      const needsInvoiceList = selectedCategory.mandatory_input_fields.some(
        (f) => f.selection_source === "stockist_invoices",
      );
      if (!needsInvoiceList) return;
      try {
        const res = await getRejectStockistInvoices({ stockist_invoice_attachment_id: siaId }, company_code);
        setStockistInvoices(res?.data?.data ?? []);
      } catch {
        /* non-blocking */
      }
    };

    // Division dropdown options, scoped to the invoice's company.
    const loadDivisions = async () => {
      const needsDivisions = selectedCategory.mandatory_input_fields.some(
        (f) => f.selection_source === "divisions",
      );
      if (!needsDivisions) return;
      try {
        const res = await queryDivisionDetails(
          `/details/?fields=division_name&company_code=${company_code ?? ""}`,
        );
        const body: any = res?.data ?? res;
        setDivisions(Array.isArray(body) ? body : body?.data ?? []);
      } catch {
        /* non-blocking */
      }
    };

    // Customer PO dropdown options from po_ref_listing (same source as the header's PO Ref No).
    const loadPoRefs = async () => {
      if (!selectedCategory.mandatory_input_fields.some(isPoNumberField)) return;
      if (!invoiceData?.counter_code) return;
      try {
        const res = await getPoRefNoListing({
          counter_code: invoiceData?.counter_code,
          location_code: invoiceData?.location_code,
          invoice_date: invoiceData?.invoice?.new_invoice_date,
          company_code,
        });
        const rows: any[] = Array.isArray(res?.data?.data) ? res.data.data : [];
        const refs: { po_number: string; po_date: string; order_code: string }[] = [];
        for (const r of rows) {
          const po_number = String(r?.poid_ref_number ?? "").trim();
          if (!po_number || refs.some((p) => p.po_number === po_number)) continue;
          refs.push({
            po_number,
            po_date: formatDateForInput(r?.poid_date ?? r?.po_date),
            order_code: r?.order_code ?? "",
          });
        }
        setListedPoRefs(refs);
      } catch {
        /* non-blocking */
      }
    };

    loadPrefill();
    loadStockistInvoices();
    loadDivisions();
    loadPoRefs();
  }, [step, selectedCategory]);

  // ── Fetch product price when product_code changes (PRC codes) ─────────────
  const fetchProductPrice = useCallback(
    async (itemCode: string) => {
      if (!selectedCategory || !PRC_CODES.includes(selectedCategory.rejection_code)) return;
      if (!itemCode.trim()) {
        setProductPrice(null); setPriceOptions([]); setSelectedPriceIdx(0); setNeedPtrBilled(false);
        return;
      }
      setIsFetchingPrice(true);
      // Reset any previously typed PTR Billed when the product changes.
      setMandatoryInputs((prev) => ({ ...prev, ptr_billed: "" }));
      try {
        const res = await getRejectProductPrice(
          { stockist_invoice_attachment_id: siaId, item_code: itemCode },
          company_code,
        );
        const data: PriceComparison | null = res?.data?.data ?? null;
        // A 0/null billed PTR means it couldn't be derived — let the reviewer
        // type it. Normalise to null so the table shows "—" until entered.
        const billed = data && Number(data.ptr_billed) > 0 ? Number(data.ptr_billed) : null;
        setProductPrice(data ? { ...data, ptr_billed: billed } : null);
        setNeedPtrBilled(!!data && billed == null);
        setPriceOptions([]);
        setSelectedPriceIdx(0);
      } catch (err: any) {
        // HTTP 400 → the product isn't on this invoice. Fall back to the company
        // master price (/get-product-price) for the same item_code so the
        // reviewer still sees MRP/PTR with no extra action. Multiple rows → let
        // them pick; the first is pre-selected.
        if (err?.response?.status === 400) {
          try {
            const priceRes: any = await getInvoicePriceList(itemCode);
            const options: ProductPriceOption[] = (priceRes?.data?.data ?? []).map(mapPriceOption);
            if (options.length > 0) {
              setPriceOptions(options);
              setSelectedPriceIdx(0);
              setProductPrice(priceOptionToComparison(options[0]));
              setNeedPtrBilled(true);
              return;
            }
          } catch {
            /* fall through to the not-found toast below */
          }
        }
        setProductPrice(null);
        setPriceOptions([]);
        setSelectedPriceIdx(0);
        setNeedPtrBilled(false);
        toast({ description: "Product not found in this invoice", variant: "destructive" });
      } finally {
        setIsFetchingPrice(false);
      }
    },
    [selectedCategory, siaId, company_code],
  );

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleCategorySelect = (cat: RejectionCategory) => {
    if (cat?.rejection_code === selectedCategory?.rejection_code) return;
    setSelectedCategory(cat);
  };

  const handleInputChange = (key: string, value: any) => {
    setMandatoryInputs((prev) => ({ ...prev, [key]: value }));
    if (key === "product_code" && selectedCategory && PRC_CODES.includes(selectedCategory.rejection_code)) {
      if (value?.length >= 3) fetchProductPrice(value);
      else { setProductPrice(null); setPriceOptions([]); setSelectedPriceIdx(0); setNeedPtrBilled(false); }
    }
  };

  const goToStep2 = () => {
    if (!selectedCategory) return;
    const hasFields = selectedCategory.mandatory_input_fields?.length > 0;
    if (hasFields) {
      setStep(2);
    } else {
      goToStep3();
    }
  };

  // Sub-remark templates only substitute {{word}} tokens, but PO fields may be
  // configured with keys like "PO Reference Number/PO ID Number". Also send the
  // PO values under stable aliases so {{customer_po_number}} / {{po_date}} resolve.
  const withPoAliases = (inputs: Record<string, any>): Record<string, any> => {
    const fields = selectedCategory?.mandatory_input_fields ?? [];
    const poNumberField = fields.find(isPoNumberField);
    const poDateField = fields.find((f) => isPoDateField(f) && f.key !== poNumberField?.key);
    const aliases: Record<string, any> = {};
    if (poNumberField && inputs[poNumberField.key]) {
      aliases.customer_po_number = inputs[poNumberField.key];
      aliases.poid_ref_number = inputs[poNumberField.key];
    }
    if (poDateField && inputs[poDateField.key]) {
      aliases.po_date = inputs[poDateField.key];
      aliases.poid_date = inputs[poDateField.key];
    }
    // Same for the invoice number/date (PO-001 configures them under non-token keys) so
    // {{invoice_number}} / {{invoice_date}} resolve instead of rendering as NULL.
    const invNumberField = fields.find(
      (f) => !isPoNumberField(f) && f.type !== "date" && (/invoice.?(number|no)/i.test(f.key || "") || /invoice.?(number|no)/i.test(f.label || "")),
    );
    const invDateField = fields.find(
      (f) => f.type === "date" && !isPoDateField(f) && (/invoice.?date/i.test(f.key || "") || /invoice.?date/i.test(f.label || "")),
    );
    if (invNumberField && inputs[invNumberField.key]) aliases.invoice_number = inputs[invNumberField.key];
    if (invDateField && inputs[invDateField.key]) aliases.invoice_date = inputs[invDateField.key];
    return { ...aliases, ...inputs };
  };

  const goToStep3 = async () => {
    if (!selectedCategory) return;
    setIsLoading(true);
    // Only when the billed PTR had to be supplied manually (product not on the
    // invoice, or it came back 0/null) do we pass the resolved price comparison
    // inside mandatory_inputs. The old flow's payload is left untouched.
    const preflightInputs = needPtrBilled
      ? {
          ...mandatoryInputs,
          inv_mrp: productPrice?.inv_mrp ?? null,
          ptr_billed: productPrice?.ptr_billed ?? null,
          ptr_as_per_company: productPrice?.ptr_as_per_company ?? null,
          gst: productPrice?.gst ?? null,
        }
      : mandatoryInputs;
    try {
      const res = await getRejectPreflight(
        {
          stockist_invoice_attachment_id: siaId,
          rejection_code: selectedCategory.rejection_code,
          mandatory_inputs: JSON.stringify(withPoAliases(preflightInputs)),
        },
        company_code,
      );
      setPreflight(res?.data?.data ?? null);
      setStep(3);
    } catch (err: any) {
      toast({
        description: err?.response?.data?.message ?? "Preflight check failed",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedCategory) return;
    setIsLoading(true);
    // Only when the billed PTR had to be supplied manually (product not on the
    // invoice, or it came back 0/null) do we send the resolved price comparison
    // inside mandatory_inputs. The old flow's payload is left untouched.
    const submitInputs = needPtrBilled
      ? {
          ...mandatoryInputs,
          inv_mrp: preflight?.price_comparison?.inv_mrp ?? productPrice?.inv_mrp ?? null,
          ptr_billed: preflight?.price_comparison?.ptr_billed ?? productPrice?.ptr_billed ?? null,
          ptr_as_per_company:
            preflight?.price_comparison?.ptr_as_per_company ?? productPrice?.ptr_as_per_company ?? null,
          gst: preflight?.price_comparison?.gst ?? productPrice?.gst ?? null,
        }
      : mandatoryInputs;
    try {
      if (mode === "rename") {
        await submitRejectionReEditAction(
          {
            event_type: "STOCKIST_ATTACHMENT_REJECTION_RE_EDIT_ACTION",
            primary_key: siaId,
            data: {
              stockist_invoice_attachment_id: siaId,
              rejection_code: selectedCategory.rejection_code,
              mandatory_inputs: withPoAliases(submitInputs),
            },
          },
          company_code,
        );
        toast({ description: "Rejection updated successfully", variant: "default" });
      } else {
        const rejectRes = await submitRejectInvoice(
          {
            event_type: "STOCKIST_ATTACHMENT_REJECT",
            primary_key: siaId,
            data: {
              stockist_invoice_attachment_id: siaId,
              rejection_code: selectedCategory.rejection_code,
              mandatory_inputs: withPoAliases(submitInputs),
            },
          },
          company_code,
        );

        // Wait for the reject event to be consumed, then record the rejection
        // reason as an invoice remark. Best-effort — a remark failure must not
        // surface as a rejection failure.
        try {
          const streamId =
            rejectRes?.data?.data?.eventStreamCreated?.streamId ?? `stockist_attachment_${siaId}`;
          const invoiceId = invoiceData?.invoice?.invoice_id ?? invoiceData?.invoice_id ?? null;
          const rejectionLabel =
            [selectedCategory.rejection_header, selectedCategory.rejection_remark].filter(Boolean).join(" - ") ||
            selectedCategory.rejection_code;
          const payloadRemarks = {
            data: {
              invoice_remarks_hdr: 22,
              invoice_id: invoiceId,
              stockist_invoice_attachment_id: siaId,
              freetxt_remarks: `${selectedCategory.rejection_code} - ${rejectionLabel}`,
              remarkby_type: idTokenDetails?.["custom:role"],
              is_private: false,
            },
            primary_key: siaId,
            event_type: "STOCKIST_INVOICE_REMARK_ADD",
          };
          await withEventCheck(
            () => postInvoiceRemark(payloadRemarks, company_code),
            streamId,
            Number(company_code),
          );
        } catch (remarkErr) {
          console.error("Failed to post rejection remark:", remarkErr);
        }

        toast({
          description: selectedCategory.is_escalation
            ? "Invoice escalated successfully"
            : "Invoice rejected successfully",
          variant: "default",
        });
      }
      onSuccess?.();
      onCloseModal();
    } catch (err: any) {
      toast({
        description:
          err?.response?.data?.message ??
          (mode === "rename" ? "Failed to update rejection" : "Failed to reject invoice"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────
  const categoryOptions = Object.values(grouped)
    .flat()
    .map((c) => ({ ...c, display_label: `${c.rejection_header} - ${c.rejection_remark}` }));
  const isStep2Valid = selectedCategory?.mandatory_input_fields?.every((f) => {
    const val = mandatoryInputs[f.key];
    if (Array.isArray(val)) return val.length > 0;
    return val !== undefined && val !== null && val !== "";
  }) ?? true;
  const isEscalation = selectedCategory?.is_review_required ?? false;
  const actionLabel =
    mode === "rename" ? "Update" : isEscalation ? "Escalation" : "Rejection";

  // Step 3 price comparison: the backend preflight often omits MRP/PTR(Company)/
  // GST, so fall back to the values we resolved on Step 2 (productPrice). Prefer
  // preflight when present; otherwise use the client-side value.
  const step3PriceComparison: PriceComparison | null =
    preflight?.price_comparison || productPrice
      ? {
          inv_mrp: preflight?.price_comparison?.inv_mrp ?? productPrice?.inv_mrp ?? null,
          ptr_billed: preflight?.price_comparison?.ptr_billed ?? productPrice?.ptr_billed ?? null,
          ptr_as_per_company:
            preflight?.price_comparison?.ptr_as_per_company ?? productPrice?.ptr_as_per_company ?? null,
          gst: preflight?.price_comparison?.gst ?? productPrice?.gst ?? null,
        }
      : null;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-y-3 w-[560px] mx-auto bg-white p-5 border border-gray-300 shadow-lg rounded-lg max-h-[90vh]">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-base font-semibold text-[#1B1A1F]">
            {mode === "rename"
              ? "Edit Rejection"
              : isEscalation
              ? "Escalate Invoice"
              : "Reject Invoice"}
          </p>
          <p className="text-xs text-[#666666]">
            Step {step} of {selectedCategory?.mandatory_input_fields?.length ? "3" : "2"}
          </p>
        </div>
        <Button className="bg-transparent -mr-3" onClick={onCloseModal}>
          <img src={crossIcon} alt="close" className="w-5 h-5 font-bold invert cursor-pointer" />
        </Button>
      </div>

      {/* Step progress indicator */}
      <div className="flex gap-1">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`h-1 flex-1 rounded-full ${s <= step ? "bg-[#8051FF]" : "bg-gray-200"}`}
          />
        ))}
      </div>

      {/* ── STEP 1: Select rejection type ── */}
      {step === 1 && (
        <div className="flex flex-col gap-y-3">
          <div>
            <p className="text-sm text-[#333] mb-1">Rejection Header - Rejection Remark</p>
            <Autocomplete
              key={`category-${selectedCategory?.rejection_code || "empty"}`}
              inputClassName="w-full"
              placeholder="Select a rejection"
              suggestionList={categoryOptions}
              displayKey="display_label"
              uniqueKey="rejection_code"
              defaultValue={
                selectedCategory
                  ? { ...selectedCategory, display_label: `${selectedCategory.rejection_header} - ${selectedCategory.rejection_remark}` }
                  : undefined
              }
              onSelectionChange={(e: any) => {
                if (!e) {
                  setSelectedCategory(null);
                  return;
                }
                handleCategorySelect(e);
              }}
            />
          </div>

          {selectedCategory && (
            <div className="bg-[#f5f0ff] border border-[#d8caff] rounded-md p-3 text-sm text-[#444]">
              <span className="font-medium text-[#8051FF]">{selectedCategory.rejection_code}</span>
              {selectedCategory.corrective_hint && (
                <p className="mt-1 text-xs text-[#666]">{selectedCategory.corrective_hint}</p>
              )}
            </div>
          )}

          <Button
            disabled={!selectedCategory}
            className="text-white bg-[#8051FF] mt-1"
            onClick={goToStep2}
          >
            Next
          </Button>
        </div>
      )}

      {/* ── STEP 2: Fill mandatory inputs ── */}
      {step === 2 && selectedCategory && (
        <div className="flex flex-col gap-y-3 pr-1">
          <p className="text-sm text-[#333] font-medium">
            Fill in the required details for{" "}
            <span className="text-[#8051FF]">{selectedCategory.rejection_code}</span>
          </p>

          {selectedCategory.mandatory_input_fields.map((field, fieldIndex) => {
            const value = mandatoryInputs[field.key] ?? "";
            const refLabel = getReferenceLabel(field.prefill_source, prefillData);

            return (
              <div key={field.key}>
                <p className="text-sm text-[#333] mb-1">{field.label}</p>

                {isPoNumberField(field) ? (
                  // FUID-mapped POs + po_ref_listing results; free text still allowed.
                  <Autocomplete
                    key={`${field.key}-${poOptions.length}-${value || "empty"}`}
                    inputClassName="w-full"
                    placeholder="Select or type customer PO number"
                    suggestionList={poOptions}
                    displayKey="po_number"
                    uniqueKey="po_number"
                    allowCustomValue
                    customFieldName="po_number"
                    defaultValue={value ? { po_number: value } : undefined}
                    onSelectionChange={(e: any) => {
                      const poNumber = String(e?.po_number ?? "").trim();
                      const picked = poOptions.find((p) => p.po_number === poNumber);
                      setMandatoryInputs((prev) => {
                        const next = { ...prev, [field.key]: poNumber };
                        // Picking a known PO also fills its date (when the source carries one).
                        if (picked?.po_date) for (const k of poDateFieldKeys) next[k] = picked.po_date;
                        return next;
                      });
                    }}
                  />
                ) : field.type === "selection" && field.selection_source === "stockist_invoices" ? (
                  <Autocomplete
                    key={`${field.key}-${value || "empty"}`}
                    inputClassName="w-full"
                    placeholder="Select or type invoice number"
                    suggestionList={stockistInvoices.map((inv) => ({ value: inv }))}
                    displayKey="value"
                    uniqueKey="value"
                    allowCustomValue
                    customFieldName="value"
                    // usePortal
                    defaultValue={value ? { value } : undefined}
                    onSelectionChange={(e: any) =>
                      handleInputChange(field.key, e?.value ? String(e.value).trim() : "")
                    }
                  />
                ) : field.type === "selection" && field.selection_source === "divisions" ? (
                  <Autocomplete
                    key={`${field.key}-${value || "empty"}`}
                    inputClassName="w-full"
                    placeholder="Select division"
                    suggestionList={divisions}
                    displayKey="division_name"
                    uniqueKey="company_divisioncode"
                    // usePortal
                    defaultValue={
                      value
                        ? { company_divisioncode: value, division_name: mandatoryInputs.division_name }
                        : undefined
                    }
                    onSelectionChange={(e: any) => {
                      // Store both the division code (field key) and its name.
                      handleInputChange(field.key, e?.company_divisioncode);
                      setMandatoryInputs((prev) => ({ ...prev, division_name: e?.division_name ?? "" }));
                    }}
                  />
                ) : field.type === "multiselect" && field.selection_source === "divisions" ? (
                  (() => {
                    // The backend flags this field "multiselect" instead of "selection" to
                    // opt it into multi-pick. field.key holds an array of division codes;
                    // `${field.key}_labels` mirrors it with display names for remarks/preview text.
                    const selectedCodes: any[] = Array.isArray(value) ? value : value ? [value] : [];
                    const labelsKey = `${field.key}_labels`;
                    return (
                      <MultiSelect
                        // Stable key: must NOT depend on the current selection, or every
                        // pick remounts the combobox and its dropdown snaps shut.
                        key={field.key}
                        placeholder="Select division(s)"
                        inputClassName="w-full"
                        suggestionList={divisions}
                        displayKey="division_name"
                        uniqueKey="company_divisioncode"
                        // usePortal
                        controlledValue={divisions.filter((d) => selectedCodes.includes(d.company_divisioncode))}
                        onSelectionChange={(item: any, action: "add" | "remove") => {
                          setMandatoryInputs((prev) => {
                            const prevCodes: any[] = Array.isArray(prev[field.key]) ? prev[field.key] : [];
                            const prevLabels: any[] = Array.isArray(prev[labelsKey]) ? prev[labelsKey] : [];
                            if (action === "add") {
                              return {
                                ...prev,
                                [field.key]: [...prevCodes, item?.company_divisioncode],
                                [labelsKey]: [...prevLabels, item?.division_name],
                              };
                            }
                            return {
                              ...prev,
                              [field.key]: prevCodes.filter((c) => c !== item?.company_divisioncode),
                              [labelsKey]: prevLabels.filter((n) => n !== item?.division_name),
                            };
                          });
                        }}
                      />
                    );
                  })()
                ) : field.type === "selection" && field.selection_source === "company_products" ? (
                  <Autocomplete
                    key={`${field.key}-${value || "empty"}`}
                    inputClassName="w-full"
                    placeholder="Search product by name or code"
                    suggestionList={[]}
                    // Display "code - name" both in the dropdown and once selected.
                    // The shared Autocomplete only renders displayKey in the input
                    // box, so we feed it a combined display_label.
                    displayKey="display_label"
                    uniqueKey="item_code"
                    // usePortal
                    isOptionInactive={(item: any) => item?.active_status === "N"}
                    defaultValue={
                      value
                        ? {
                            item_code: value,
                            product_name: mandatoryInputs.product_name,
                            display_label: mandatoryInputs.product_name
                              ? `${value} - ${mandatoryInputs.product_name}`
                              : value,
                          }
                        : undefined
                    }
                    asyncListFunction={async (query: string) => {
                      const extract = (res: any): any[] => {
                        const body = res?.data?.data ?? res?.data;
                        if (Array.isArray(body)) return body;
                        if (Array.isArray(body?.products)) return body.products;
                        if (Array.isArray(body?.data)) return body.data;
                        if (Array.isArray(body?.rows)) return body.rows;
                        return [];
                      };
                      const q = query ?? "";
                      try {
                        // Search by name and code in parallel, then merge & dedupe by item_code
                        const [byName, byCode] = await Promise.all([
                          getSimilarCompanyProducts(String(company_code ?? ""), q, "", 1, 10, undefined, true),
                          q
                            ? getSimilarCompanyProducts(String(company_code ?? ""), "", q, 1, 10, undefined, true)
                            : Promise.resolve(null),
                        ]);
                        const seen = new Set<string>();
                        const merged: any[] = [];
                        for (const item of [...extract(byName), ...extract(byCode)]) {
                          const code = String(item?.item_code ?? "");
                          if (!code || seen.has(code)) continue;
                          seen.add(code);
                          // Combined label so the dropdown and the selected input
                          // both read "code - name".
                          const name = item?.product_name ?? "";
                          merged.push({
                            ...item,
                            display_label: name ? `${code} - ${name}` : code,
                          });
                        }
                        return merged;
                      } catch {
                        return [];
                      }
                    }}
                    onSelectionChange={(e: any) => {
                      handleInputChange(field.key, e?.item_code);
                      // Also include the product name alongside the product code in the payload.
                      setMandatoryInputs((prev) => ({ ...prev, product_name: e?.product_name ?? "" }));
                      // M10-15732: jump straight to the next mandatory field (e.g. MRP) on selection.
                      const nextField = selectedCategory.mandatory_input_fields[fieldIndex + 1];
                      if (nextField) {
                        setTimeout(() => mandatoryFieldInputRefs.current[nextField.key]?.focus(), 0);
                      }
                    }}
                  />
                ) : isOrderNumberField(field) && fuidOrderCodes.length > 1 ? (
                  <select
                    value={value}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-[#f7f7f7] focus:outline-none focus:ring-1 focus:ring-[#8051FF]"
                  >
                    <option value="" disabled>
                      Select order number
                    </option>
                    {/* Keep a previously saved value (rename mode) selectable even if it's no longer mapped. */}
                    {value && !fuidOrderCodes.includes(value) && <option value={value}>{value}</option>}
                    {fuidOrderCodes.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                ) : field.type === "date" ? (
                  <input
                    ref={(el:any) => { mandatoryFieldInputRefs.current[field.key] = el; }}
                    type="date"
                    value={value}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-[#f7f7f7] focus:outline-none focus:ring-1 focus:ring-[#8051FF]"
                  />
                ) : (
                  <Input
                    ref={(el:any) => { mandatoryFieldInputRefs.current[field.key] = el; }}
                    className="w-full"
                    placeholder={`Enter ${field?.label?.toLowerCase()}`}
                    value={value}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                  />
                )}

                {refLabel && (
                  <p className="text-xs text-[#888] mt-1">{refLabel}</p>
                )}
              </div>
            );
          })}

          {/* PRC fallback price selector — product not on invoice, multiple
              master price rows returned by /get-product-price. Reviewer picks
              which MRP/PTR applies; the comparison preview below reflects it. */}
          {PRC_CODES.includes(selectedCategory.rejection_code) && priceOptions.length > 1 && (
            <div>
              <p className="text-sm text-[#333] mb-1">Select MRP - PTR (company master)</p>
              <select
                value={selectedPriceIdx}
                onChange={(e) => {
                  const idx = Number(e.target.value);
                  setSelectedPriceIdx(idx);
                  // Keep the reviewer-entered PTR Billed across selection changes.
                  setProductPrice((prev) => ({
                    ...priceOptionToComparison(priceOptions[idx]),
                    ptr_billed: prev?.ptr_billed ?? null,
                  }));
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-[#f7f7f7] focus:outline-none focus:ring-1 focus:ring-[#8051FF]"
              >
                {priceOptions.map((opt, idx) => (
                  <option key={opt.company_productprice_id ?? idx} value={idx}>
                    MRP: {opt.mrp ?? "—"} | PTR: {opt.ptr ?? "—"} | GST%: {opt.gst ?? "—"}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* PRC — the billed PTR couldn't be derived (product not on the
              invoice, or the endpoint returned 0/null). Capture it from the
              reviewer; it feeds the comparison preview (and flags overbilling). */}
          {PRC_CODES.includes(selectedCategory.rejection_code) && needPtrBilled && (
            <div>
              <p className="text-sm text-[#333] mb-1">PTR Billed</p>
              <input
                ref={ptrBilledInputRef} //M10-15727
                type="number"
                placeholder="Enter PTR billed"
                value={productPrice?.ptr_billed ? productPrice.ptr_billed : ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  const num = raw === "" ? null : Number(raw);
                  setProductPrice((prev) => (prev ? { ...prev, ptr_billed: num } : prev));
                  setMandatoryInputs((prev) => ({ ...prev, ptr_billed: raw }));
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-[#f7f7f7] focus:outline-none focus:ring-1 focus:ring-[#8051FF]"
              />
            </div>
          )}

          {/* PRC price comparison preview */}
          {PRC_CODES.includes(selectedCategory.rejection_code) && productPrice && (
            <div className="border border-gray-200 rounded-md overflow-hidden text-sm mt-1">
              <p className="bg-[#f5f0ff] text-[#8051FF] font-medium px-3 py-2 text-xs">
                Price Comparison
              </p>
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[#555]">MRP</th>
                    <th className="px-3 py-2 text-left text-[#555]">PTR Billed</th>
                    <th className="px-3 py-2 text-left text-[#555]">PTR (Company)</th>
                    <th className="px-3 py-2 text-left text-[#555]">GST%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-3 py-2">{productPrice.inv_mrp ?? "—"}</td>
                    <td className={`px-3 py-2 ${productPrice.ptr_billed != null && productPrice.ptr_as_per_company != null && productPrice.ptr_billed > productPrice.ptr_as_per_company ? "text-red-600 font-medium" : ""}`}>
                      {productPrice.ptr_billed ?? "—"}
                    </td>
                    <td className="px-3 py-2">{productPrice.ptr_as_per_company ?? "—"}</td>
                    <td className="px-3 py-2">{productPrice.gst ?? "—"}</td>
                  </tr>
                </tbody>
              </table>
              {isFetchingPrice && (
                <p className="text-xs text-center text-[#888] py-2">Loading price data…</p>
              )}
            </div>
          )}

          <div className="flex gap-2 mt-1">
            <Button
              className="flex-1 bg-gray-100 text-[#333] hover:bg-gray-200"
              onClick={() => setStep(1)}
            >
              Back
            </Button>
            <Button
              disabled={!isStep2Valid || isLoading}
              isLoading={isLoading}
              loaderSize="small"
              className="flex-1 text-white bg-[#8051FF]"
              onClick={goToStep3}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* ── STEP 3: Preflight preview & confirm ── */}
      {step === 3 && preflight && (
        <div className="flex flex-col gap-y-3">
          <div className="flex flex-col gap-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {/* Rejection type */}
            <div className="bg-gray-50 rounded-md p-3 text-sm">
              <p className="text-xs text-[#888] mb-1">Rejection Type</p>
              <p className="font-medium text-[#1B1A1F]">
                {preflight.rejection_header} → {preflight.rejection_remark}
              </p>
              <p className="text-xs text-[#8051FF] mt-1">{preflight.rejection_code}</p>
            </div>

            {/* Sub remarks */}
            {preflight.sub_remarks && (
              <div className="bg-gray-50 rounded-md p-3 text-sm">
                <p className="text-xs text-[#888] mb-1">Sub Remarks</p>
                <p className="text-[#333] whitespace-pre-line">{preflight.sub_remarks}</p>
              </div>
            )}

            {/* Corrective hint */}
            {preflight.corrective_hint && (
              <div className="bg-[#f5f0ff] border border-[#d8caff] rounded-md p-3 text-sm">
                <p className="text-xs text-[#8051FF] font-medium mb-1">Remarks/Comments</p>
                <p className="text-[#444]">{preflight.corrective_hint}</p>
              </div>
            )}

            {/* Price comparison (PRC codes) */}
            {step3PriceComparison && (
              <div className="border border-gray-200 rounded-md overflow-hidden text-sm">
                <p className="bg-[#f5f0ff] text-[#8051FF] font-medium px-3 py-2 text-xs">
                  Price Comparison
                </p>
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-[#555]">MRP</th>
                      <th className="px-3 py-2 text-left text-[#555]">PTR Billed</th>
                      <th className="px-3 py-2 text-left text-[#555]">PTR (Company)</th>
                      <th className="px-3 py-2 text-left text-[#555]">GST%</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="px-3 py-2">{step3PriceComparison.inv_mrp ?? "—"}</td>
                      <td className={`px-3 py-2 ${step3PriceComparison.ptr_billed != null && step3PriceComparison.ptr_as_per_company != null && step3PriceComparison.ptr_billed > step3PriceComparison.ptr_as_per_company ? "text-red-600 font-medium" : ""}`}>
                        {step3PriceComparison.ptr_billed ?? "—"}
                      </td>
                      <td className="px-3 py-2">{step3PriceComparison.ptr_as_per_company ?? "—"}</td>
                      <td className="px-3 py-2">{step3PriceComparison.gst ?? "—"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              className="flex-1 bg-gray-100 text-[#333] hover:bg-gray-200"
              onClick={() => {
                const hasFields = selectedCategory?.mandatory_input_fields?.length ?? 0;
                setStep(hasFields > 0 ? 2 : 1);
              }}
            >
              Back
            </Button>
            <Button
              disabled={isLoading}
              isLoading={isLoading}
              loaderSize="small"
              className={`flex-1 text-white ${isEscalation ? "bg-[#FF8C00]" : "bg-[#FF4444]"}`}
              onClick={handleConfirm}
            >
              Confirm {actionLabel}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceReject;
